/* custom-shimmer.js — loading states for data that comes from the store
   (loaded on every page by snippets/custom-globals.liquid; styles in
   assets/custom-theme.css "Loading shimmer", README §11.11).

   1. Images: store images shimmer from the first paint by CSS alone; this
      script marks each one [data-q-ready] once it has loaded (other images
      get [data-q-loading] while they download). Lazy images sweep only
      while near the screen ([data-q-near]); further away they are plain grey. New images and src/srcset
      changes are picked up automatically — zoom view, quick add, cart
      drawer, search, recommendations, variant switches.
   2. Variant switch: the option label changes instantly ("Color: Blue").
      Price / "Save X%" / stock text is left as is (no shimmer on text).
   3. Catalog / search: pagination is Horizon's (assets/paginated-list.js,
      24 per page, next page pre-fetched). This only adds a row of skeleton
      cards under the grid while more pages exist, so scrolling faster than
      the next page loads shows cards-on-the-way instead of an empty gap.

   Reuse first (README §15.9): nothing here re-implements what Horizon
   already does — it plugs into Horizon's events, attributes, keyframes and
   helpers, and only fills the gaps Horizon leaves.

   Opt out: add data-q-no-shimmer to an image or any wrapper.
   Fail-safe: without this script, images still load and cover the CSS
   shimmer; nothing else changes. */

const SKIP = '[data-q-no-shimmer], .header-logo__image, .logo-block__image, .logo-section__image';
const MAX_WAIT = 20000; // never shimmer forever (stalled request)

/* ---- 1. Images ---------------------------------------------------------- */

/** @type {WeakMap<HTMLImageElement, () => void>} image → detach its listeners */
const watching = new WeakMap();

// Waiting images sweep only while near the screen ([data-q-near], see
// custom-theme.css): an animation on every unloaded lazy image down the page
// costs style work on every frame for something nobody can see.
const near =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) entry.target.toggleAttribute('data-q-near', entry.isIntersecting);
        },
        { rootMargin: '200px' }
      )
    : null;

/** @param {HTMLImageElement} img */
function release(img) {
  watching.get(img)?.();
  watching.delete(img);
  near?.unobserve(img);
  img.removeAttribute('data-q-near');
}

/** @param {HTMLImageElement} img */
function ready(img) {
  img.removeAttribute('data-q-loading');
  img.setAttribute('data-q-ready', '');
}

/** @param {Element} img */
function track(img) {
  if (!(img instanceof HTMLImageElement) || watching.has(img)) return;
  if (img.closest(SKIP)) {
    img.setAttribute('data-q-ready', '');
    return;
  }

  // Loaded (or already failed): nothing to show
  if (img.complete) {
    ready(img);
    return;
  }

  img.removeAttribute('data-q-ready');
  img.setAttribute('data-q-loading', '');
  if (near) near.observe(img);
  else img.setAttribute('data-q-near', '');

  /** @param {Event} [event] */
  const finish = (event) => {
    release(img);
    ready(img);
    if (event?.type === 'load') {
      // Soft fade-in, then tidy up so a later src change can fade again
      img.setAttribute('data-q-loaded', '');
      img.addEventListener('animationend', () => img.removeAttribute('data-q-loaded'), { once: true });
    }
  };
  const timer = setTimeout(finish, MAX_WAIT);
  img.addEventListener('load', finish);
  img.addEventListener('error', finish);
  watching.set(img, () => {
    clearTimeout(timer);
    img.removeEventListener('load', finish);
    img.removeEventListener('error', finish);
  });
}

/** @param {Document | Element} root */
function scan(root) {
  if (root instanceof HTMLImageElement) track(root);
  root.querySelectorAll('img').forEach(track);
}

/* ---- 3. Next-page skeleton row ------------------------------------------ */

const GRID = '[infinite-scroll="true"] .product-grid[data-last-page]';
/** @type {WeakMap<Element, HTMLElement>} grid → its skeleton row */
const skeletons = new WeakMap();

/** @param {Element} grid */
function syncGridSkeleton(grid) {
  const pages = [...grid.querySelectorAll(':scope > [data-page]')].map((card) => Number(card.getAttribute('data-page')) || 0);
  const lastPage = Number(grid.getAttribute('data-last-page')) || 1;
  const moreToCome = pages.length > 0 && Math.max(...pages) < lastPage;

  let row = skeletons.get(grid);
  if (!moreToCome) {
    if (row) row.hidden = true;
    return;
  }

  if (!row || !row.isConnected) {
    // Same classes/style as the real grid, so columns and gaps match exactly
    row = /** @type {HTMLElement} */ (grid.cloneNode(false));
    ['ref', 'data-testid', 'id', 'data-last-page', 'role'].forEach((a) => row.removeAttribute(a));
    row.classList.add('q-grid-skeleton');
    row.setAttribute('aria-hidden', 'true');
    const anchor = grid.parentElement?.querySelector(':scope > [ref="viewMoreNext"]') ?? grid;
    anchor.after(row);
    skeletons.set(grid, row);
  }

  // One row of placeholders, as many as the grid has columns, one row-gap below
  const gridStyle = getComputedStyle(grid);
  const columns = gridStyle.gridTemplateColumns.split(' ').filter(Boolean).length || 2;
  row.style.marginBlockStart = gridStyle.rowGap;
  while (row.children.length < columns) {
    const item = document.createElement('li');
    item.innerHTML = '<div class="q-skeleton-card"></div>';
    row.append(item);
  }
  while (row.children.length > columns) row.lastElementChild?.remove();
  row.hidden = false;
}

let gridFrame = 0;
function syncGrids() {
  cancelAnimationFrame(gridFrame);
  gridFrame = requestAnimationFrame(() => document.querySelectorAll(GRID).forEach(syncGridSkeleton));
}

/* ---- Wiring --------------------------------------------------------------- */

scan(document);
syncGrids();
addEventListener('resize', syncGrids, { passive: true });

new MutationObserver((mutations) => {
  let gridsChanged = false;
  for (const m of mutations) {
    if (m.type === 'attributes') {
      // New src/srcset (variant switch, responsive swap): watch it afresh
      if (m.target instanceof HTMLImageElement) {
        release(m.target);
        track(m.target);
      }
      continue;
    }
    m.addedNodes.forEach((node) => node instanceof Element && scan(node));
    // Page appended, filters changed, section re-rendered…
    if (!gridsChanged && m.target instanceof Element && !m.target.closest('.q-grid-skeleton')) {
      gridsChanged = !!m.target.closest('results-list') || [...m.addedNodes].some((n) => n instanceof Element && !!n.querySelector?.('.product-grid'));
    }
  }
  if (gridsChanged) syncGrids();
}).observe(document.documentElement, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['src', 'srcset'],
});

/* ---- 2. Variant switch ---------------------------------------------------- */

// Horizon's ProductSelectEvent (StandardEvents.productSelect). A string, so a
// failed import can never take the image shimmer down with it.
// Text (price, "Save X%", stock) deliberately gets NO shimmer: it updates in
// a moment and the old value stays readable — a sweep over it is just noise.
const PRODUCT_SELECT = 'shopify:product:select';

document.addEventListener(PRODUCT_SELECT, (event) => {
  const picker = event.target;
  // Swatches inside product cards are not a product-page selection
  if (!(picker instanceof Element) || picker.closest('product-card')) return;

  // Label follows the tap immediately ("Color: Blue"); the server re-render
  // that follows writes the same value.
  picker.querySelectorAll('fieldset').forEach((fieldset) => {
    const value = fieldset.querySelector('input:checked')?.value;
    const label = fieldset.querySelector('legend .variant-option__swatch-value');
    if (value && label) label.textContent = value;
  });
});
